/**
 * feed/api.ts — typed client for the B-WP11b `feed.*` surface.
 *
 * Transport: same-origin authed oRPC (`baseUrl: ""`) via createApiClient —
 * credentials + CSRF ride along (groups/api.ts, friends/api.ts idiom).
 *
 * `feed.list {lane}` returns one lane's page; the two lanes have distinct sort
 * keys, so each paginates independently and the FeedView merges the two
 * default-on lanes client-side by timestamp. `feed.retract` is a mutation (used
 * by the version-row audience editor, not by the FeedView) and THROWS on failure
 * (Rule 8).
 *
 * Build plan: .plan/implementation/2026-07-10-track-b-social-build-plan.md B-WP11b.
 */

import { createApiClient } from "@proof/client-core/api";
import { createGroupsApi } from "~/features/groups/api";

function client() {
  return createApiClient({ baseUrl: "" });
}

export type FeedLane = "friends" | "following";
export type FeedEventKind = "recipe_published" | "go_to_updated" | "spinoff_published";

export interface FeedItem {
  lane: FeedLane;
  id: string;
  ownerHandle: string;
  recipeUuid: string;
  recipeTitle: string;
  /** /r/:recipeUuid target — present ONLY when the recipe is public. */
  publicUuid: string | null;
  rating: number | null;
  noteExcerpt: string | null;
  message: string | null;
  photoMediaKey: string | null;
  cookedAt: number | null;
  deliveredAt: number | null;
  eventKind: FeedEventKind | null;
  createdAt: number | null;
}

export interface FeedPage {
  items: FeedItem[];
  nextCursor: string | null;
}

/** Load one lane's page. A read failure bubbles to the caller (FeedView degrades). */
export async function loadFeedLane(lane: FeedLane, cursor?: string): Promise<FeedPage> {
  const res = await client().feed.list(cursor ? { lane, cursor } : { lane });
  return { items: res.items as FeedItem[], nextCursor: res.nextCursor };
}

/**
 * Retract a cook from friends' feeds (audience tighten). MUTATION — throws on
 * failure so the version-row editor surfaces a visible error (Rule 8).
 */
export async function retractCook(attemptUuid: string): Promise<void> {
  await client().feed.retract({ attemptUuid });
}

/** The frozen cook coordinates the loosen path needs to re-fan-out. */
export interface ShareCookToFriendsInput {
  recipeUuid: string;
  variationUuid: string;
  attemptUuid: string;
}

/**
 * Loosen a finalized cook's audience to friends (audience LOOSEN — vision v3).
 * Routes through the SAME `group.reports.publish` path with `friends:true` and
 * an empty `groupUuids` (a friends-only late share), so the fan-out timestamps
 * every delivered row by the FROZEN `cooked_at` — a late share never jumps the
 * queue (v6). rating / note / cooked_at are server-derived; the input carries
 * only the cook coordinates.
 *
 * MUTATION — THROWS when the friends outcome is not `delivered` (a compensated
 * fan-out failure), so the version-row editor surfaces a visible error and
 * reverts its optimistic state (Rule 8). A silent `error` outcome would leave
 * the toggle claiming a delivery that never happened.
 */
export async function shareCookToFriends(input: ShareCookToFriendsInput): Promise<void> {
  const { friends } = await createGroupsApi("").publishReports({
    recipeUuid: input.recipeUuid,
    variationUuid: input.variationUuid,
    attemptUuid: input.attemptUuid,
    groupUuids: [],
    friends: true,
  });
  if (friends?.status !== "delivered") {
    throw new Error("friends_audience_not_delivered");
  }
}

/** The sort timestamp for an item: cook date (friends) or event date (following). */
export function feedSortTs(item: FeedItem): number {
  return item.lane === "friends" ? (item.cookedAt ?? 0) : (item.createdAt ?? 0);
}

// ── Nod (B-WP11c) ─────────────────────────────────────────────────────────────

/**
 * Toggle the caller's nod on a delivered cook. MUTATION — throws on failure so
 * the card surfaces a visible error and reverts its pressed state (Rule 8).
 */
export async function toggleNod(attemptUuid: string, on: boolean): Promise<void> {
  await client().feed.nod({ attemptUuid, on });
}

/**
 * The handles that have nodded a cook (owner or live-row holder only; NOT a
 * count). A read failure bubbles to the card, which degrades to no nod line.
 */
export async function loadNodHandles(attemptUuid: string): Promise<string[]> {
  const res = await client().feed.nods.list({ attemptUuid });
  return res.handles;
}
